import React from 'react';

/**
 * Step-by-step onboarding wizard frame: a slim top bar, a horizontal progress stepper (circles +
 * connectors on desktop, a progress bar on mobile), then centered, focused content for the active
 * step. Theme-aware (light/dark). Steps pass their own inner cards where needed.
 */
const RecBadge = () => (
  <span className="flex items-center gap-1.5 rounded-full bg-rose-500/10 px-2.5 py-1 text-[11px] font-semibold text-rose-500 ring-1 ring-rose-500/20">
    <span className="h-2 w-2 animate-pulse rounded-full bg-rose-500" /> Recording
  </span>
);

const OnboardingShell = ({
  steps = [],          // [{ key, label }] — the interview step is omitted from the stepper
  activeKey,
  completed = {},
  recording = false,
  title,
  subtitle,
  children,
}) => {
  // Don't show the final "Interview" node in the onboarding stepper.
  const visible = steps.filter((s) => s.key !== 'interview');
  const activeIndex = Math.max(0, visible.findIndex((s) => s.key === activeKey));

  return (
    <div className="flex min-h-[100dvh] w-full flex-col items-center bg-slate-50 text-slate-900 dark:bg-[#0a0a12] dark:text-white">
      {/* top bar */}
      <header className="flex w-full items-center justify-between gap-3 px-5 py-4 sm:px-8">
        <div className="flex min-w-0 items-center gap-2">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 text-base font-bold text-white">W</span>
          <span className="truncate text-base font-extrabold sm:text-sm">WIZBOX <span className="font-normal text-slate-400">Interview Suite</span></span>
        </div>
        <div className="shrink-0">
          {recording ? <RecBadge /> : <span className="hidden text-[11px] text-slate-400 sm:inline">Powered by Wiza</span>}
        </div>
      </header>

      {/* progress stepper */}
      <div className="w-full max-w-2xl px-6 pt-2 sm:px-8">
        {/* desktop: circles + connectors */}
        <div className="hidden items-start sm:flex">
          {visible.map((s, i) => {
            const done = completed[s.key];
            const active = i === activeIndex;
            return (
              <React.Fragment key={s.key}>
                <div className="flex w-9 flex-col items-center">
                  <span className={`grid h-9 w-9 place-items-center rounded-full text-sm font-semibold transition
                    ${done ? 'bg-emerald-500 text-white'
                      : active ? 'bg-gradient-to-br from-primary-500 to-accent-500 text-white shadow-lg shadow-accent-500/30'
                        : 'bg-slate-200 text-slate-400 dark:bg-white/10'}`}>
                    {done ? '✓' : i + 1}
                  </span>
                  <span className={`mt-2 whitespace-nowrap text-xs ${active ? 'font-semibold' : 'text-slate-400'}`}>{s.label}</span>
                </div>
                {i < visible.length - 1 && (
                  <div className="mx-1 mt-4 h-0.5 flex-1 rounded bg-slate-200 dark:bg-white/10">
                    <div className="h-full rounded bg-emerald-500 transition-all duration-500"
                      style={{ width: i < activeIndex ? '100%' : '0%' }} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        {/* mobile: progress bar */}
        <div className="sm:hidden">
          <div className="mb-1.5 flex justify-between text-xs text-slate-400">
            <span>Step {activeIndex + 1} of {visible.length}</span>
            <span className="font-medium text-slate-500 dark:text-slate-300">{visible[activeIndex] && visible[activeIndex].label}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
            <div className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-500"
              style={{ width: `${((activeIndex + 1) / visible.length) * 100}%` }} />
          </div>
        </div>
      </div>

      {/* centered step content */}
      <main className="flex w-full max-w-2xl flex-1 flex-col px-5 py-8 text-center sm:px-8">
        {title && <h1 className="text-2xl font-bold tracking-tight">{title}</h1>}
        {subtitle && <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>}
        <div className="mt-7 flex-1 text-left">{children}</div>
      </main>
    </div>
  );
};

export default OnboardingShell;
